// ✅ src/pages/VehicleV2/VehicleAllIssueDetailsReport.jsx
// UPDATED FRONTEND
// - Uses localStorage session headers
// - Sends x-finyearid / x-finyear / x-branchid / x-branchtype / x-userid
// - Keeps server paging, export, print, filters
// - Store + Equipment Number remain required

import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaListUl,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/AllIssueDetailsRpt";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

const isoToDDMMYYYY = (s) => {
  const t = toStr(s);
  if (!t) return "";
  const [yyyy, mm, dd] = t.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
};

const isValidISODate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(toStr(s));

const exportToCsv = (rows, filename = "vehicle-issue-details.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const safe = String(cell).replace(/"/g, '""');
          return `"${safe}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function VehicleAllIssueDetailsReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin, theme } = useContext(ThemeContext);

  const currentSkin = useMemo(() => getCurrentSkin(theme || skin), [theme, skin]);
  const { baseColor, baseShade } = currentSkin || {};
  const buttonClass = `bg-${baseColor || "blue"}-${baseShade || "600"}`;
  const hoverClass = `hover:bg-${baseColor || "blue"}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const sessionBranchType = (
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO"
  ).toUpperCase();
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [meta, setMeta] = useState({
    stores: [],
    vehicles: [],
  });

  const [filters, setFilters] = useState({
    storeId: "Select",
    vehicleId: "Select",
    fromDate: "",
    toDate: "",
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- validation ---------------- */
  const validate = useCallback((f) => {
    if (toStr(f.storeId).toLowerCase() === "select" || !toStr(f.storeId)) {
      toast.error("Please select Store");
      return false;
    }

    if (toStr(f.vehicleId).toLowerCase() === "select" || !toStr(f.vehicleId)) {
      toast.error("Please select Equipment Number");
      return false;
    }

    const fd = toStr(f.fromDate);
    const td = toStr(f.toDate);

    if (fd && !isValidISODate(fd)) {
      toast.error("Invalid From Date");
      return false;
    }

    if (td && !isValidISODate(td)) {
      toast.error("Invalid To Date");
      return false;
    }

    if (fd && td) {
      const fromT = Date.parse(fd);
      const toT = Date.parse(td);
      if (Number.isFinite(fromT) && Number.isFinite(toT) && toT < fromT) {
        toast.error("Please select to date greater than from date");
        return false;
      }
    }

    return true;
  }, []);

  /* ---------------- params builder ---------------- */
  const buildCommonParams = useCallback(
    (f) => ({
      branchId: toStr(sessionBranchId || ""),
      branchType: sessionBranchType || "HO",
      loginUserId: toStr(loginUserId || ""),
      vehicleId: toStr(f.vehicleId),
      storeId: toStr(f.storeId),
      fromDate: toStr(f.fromDate),
      toDate: toStr(f.toDate),
    }),
    [sessionBranchId, sessionBranchType, loginUserId]
  );

  /* ---------------- APIs ---------------- */
  const loadMeta = useCallback(async () => {
    try {
      const rs = await axios.get(`${API_BASE}/meta`, {
        headers: apiHeaders,
        params: {
          branchId: sessionBranchId || "",
          branchType: sessionBranchType || "HO",
          loginUserId: loginUserId || "",
        },
      });

      if (!rs.data?.success) {
        throw new Error(rs.data?.message || "Meta load failed");
      }

      setMeta({
        stores: Array.isArray(rs.data.stores) ? rs.data.stores : [],
        vehicles: Array.isArray(rs.data.vehicles) ? rs.data.vehicles : [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setMeta({ stores: [], vehicles: [] });
    }
  }, [apiHeaders, sessionBranchId, sessionBranchType, loginUserId]);

  const loadList = useCallback(
    async (f = filters, p = 1, ps = pageSize, showToast = false) => {
      try {
        setLoading(true);

        if (!validate(f)) {
          setRows([]);
          setTotal(0);
          return;
        }

        const params = {
          ...buildCommonParams(f),
          page: toInt(p, 1),
          pageSize: toInt(ps, 10),
        };

        const rs = await axios.get(`${API_BASE}/list`, {
          headers: apiHeaders,
          params,
        });

        if (!rs.data?.success) {
          throw new Error(rs.data?.message || "List load failed");
        }

        setRows(Array.isArray(rs.data.rows) ? rs.data.rows : []);
        setTotal(toInt(rs.data.total ?? 0, 0));
        setPage(toInt(p, 1));
        setPageSize(toInt(ps, 10));

        if (showToast) {
          toast.success(`Loaded ${rs.data.total ?? (rs.data.rows || []).length} records`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load grid");
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [apiHeaders, buildCommonParams, filters, pageSize, validate]
  );

  /* ---------------- init ---------------- */
  useEffect(() => {
    const init = async () => {
      try {
        setPageLoading(true);
        await loadMeta();
      } finally {
        setPageLoading(false);
      }
    };

    init();
  }, [loadMeta]);

  /* ---------------- handlers ---------------- */
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleClear = () => {
    setFilters({
      storeId: "Select",
      vehicleId: "Select",
      fromDate: "",
      toDate: "",
    });
    setRows([]);
    setTotal(0);
    setPage(1);
    toast.info("Filters cleared");
  };

  const handleSearch = async () => {
    await loadList(filters, 1, pageSize, true);
  };

  const gotoPage = async (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    await loadList(filters, next, pageSize, false);
  };

  const handlePrint = () => {
    if (!validate(filters)) return;

    const qs = new URLSearchParams({
      vehicleId: toStr(filters.vehicleId),
      fromDate: isoToDDMMYYYY(toStr(filters.fromDate)),
      toDate: isoToDDMMYYYY(toStr(filters.toDate)),
      storeId: toStr(filters.storeId),
    }).toString();

    window.open(`/vehicle/VehicleAllIssueDetailReport_Print?${qs}`, "_blank");
  };

  const handleExport = () => {
    if (!rows.length) {
      toast.info("No rows to export");
      return;
    }
    exportToCsv(rows, "vehicle-issue-details-page.csv");
    toast.success("Exported CSV");
  };

  const headerCount = useMemo(() => rows.length, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE ISSUE DETAILS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Issue Details Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                Rows (this page): {headerCount}
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-emerald-900 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                Total Records: {total}
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"
                }`}
              >
                INFRA SCREEN : Vehicle Issue Detail Report
              </span>

              {(loading || pageLoading) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>
                    Store <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleSelectChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {meta.stores.map((s) => (
                      <option key={s.value ?? s.label} value={s.value ?? s.label}>
                        {s.label ?? s.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleDateChange}
                    className={inputClass}
                  />
                  {!!filters.fromDate && (
                    <div className={`mt-1 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {isoToDDMMYYYY(filters.fromDate)}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Equipment Number <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="vehicleId"
                    value={filters.vehicleId}
                    onChange={handleSelectChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {meta.vehicles.map((v) => (
                      <option key={v.value ?? v.label} value={v.value ?? v.label}>
                        {v.label ?? v.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleDateChange}
                    className={inputClass}
                  />
                  {!!filters.toDate && (
                    <div className={`mt-1 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {isoToDDMMYYYY(filters.toDate)}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
                <button
                  onClick={handleClear}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-3 flex-wrap justify-end">
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                    disabled={loading}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search Records"}
                  </button>

                  <button
                    type="button"
                    onClick={handlePrint}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                    disabled={loading}
                  >
                    <FaPrint />
                    Print
                  </button>

                  <button
                    type="button"
                    onClick={handleExport}
                    className={`flex items-center gap-2 border px-6 py-3 rounded-lg font-semibold ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaFileExport />
                    Export Page CSV
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
              <FaListUl /> Vehicle Issue Details (Search List)
            </h2>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {loading ? "Loading..." : total > 0 ? `Page ${page} / ${totalPages}` : "No search yet"}
            </div>
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-14">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-8 h-8 text-gray-400" />
                </div>
                <div className={`text-lg font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </div>
                <div className={`mt-2 text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Select Store + Equipment Number and click Search.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">S.No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Code</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Reg No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Date</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Item Name</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Qty</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Price</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Serial No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Battery Type</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Battery Make</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Supplier</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Tyre Make</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Tyre Model</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Amount</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Store</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const vehName = pick(r, ["vehiclename", "VehicleName", "fld_vehicle_name"]);
                      const vehCode = pick(r, ["vehiclecode", "VehicleCode", "fld_vehicle_code"]);
                      const regNo = pick(r, ["vehicleregno", "VehicleRegNo", "fld_vehicle_regno"]);
                      const date = pick(r, ["fld_date", "date", "issuedate"]);
                      const item = pick(r, ["fld_itemname", "itemname"]);
                      const qty = pick(r, ["fld_qty", "qty"], "0");
                      const price = pick(r, ["fld_price", "price"], "0");
                      const serialNo = pick(r, ["fld_serialno", "serialno", "fld_batteryserialno"]);
                      const batteryType = pick(r, ["fld_batterytype", "batterytype"]);
                      const batteryMake = pick(r, ["fld_batterymake", "batterymake"]);
                      const supplier = pick(r, ["fld_suppliername", "suppliername"]);
                      const tyreMake = pick(r, ["fld_tyremake", "tyremake"]);
                      const tyreModel = pick(r, ["fld_tyre_model", "tyremodel"]);
                      const amount = pick(r, ["fld_amount", "amount"], "0");
                      const storeName = pick(r, ["fld_storename", "storename"]);

                      return (
                        <tr
                          key={`${vehCode}-${idx}`}
                          className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {vehName || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {vehCode || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {regNo || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {date || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {item || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {qty}
                          </td>

                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {price}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {serialNo || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {batteryType || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {batteryMake || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {supplier || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {tyreMake || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {tyreModel || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {amount}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {storeName || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > 0 && (
            <div className="mt-5 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-3 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} entries
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={async (e) => {
                    const ps = toInt(e.target.value, 10);
                    setPageSize(ps);
                    await loadList(filters, 1, ps, false);
                  }}
                  className={inputClass}
                  style={{ width: 110 }}
                >
                  {[5, 10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => gotoPage(page - 1)}
                  disabled={page <= 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    page <= 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                <button
                  onClick={() => gotoPage(page + 1)}
                  disabled={page >= totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    page >= totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}